import { FormEvent, useEffect, useRef, useState } from 'react'
import { useReactToPrint } from "react-to-print";
import { Form, NavLink, useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import QRCode from "react-qr-code";
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { ICustomerLight } from '../../types/customer';
import { IMaterialInventory, IMaterialInventoryLoad, IMaterialInventoryAssignment } from '../../types/material';
import { IMaterialOption, ISelectOption } from '../../types/list';


import { ErrorProvider } from '../../classes/errorProvider';
import { useListContext } from '../../contexts/listContext';
import { useUserContext } from '../../contexts/userContext';
import { CustomerService } from '../../services/customerService';
import { LoadService } from '../../services/loadService';
import { MaterialPriceService } from '../../services/materialPriceService';
import { UtilityService } from '../../services/utilityService';
import { VendorService } from '../../services/vendorService';

import Spinner from '../spinner';
import { Icons } from '../icons';
import NumberField from '../shared/numberField';
import Json from '../json';

import loadPositionMap from '../../../src/assets/LoadPositionMap.jpg';

import '../../styles/materialInventoryLabelForm.scss';
import { AppConfig } from '../../config/appConfig';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface IMaterialInventoryLabelFormProps {
	id?: string;
	isEditingChange?: (isEditing: boolean) => void;
}

const MaterialInventoryLabelForm: React.FunctionComponent<IMaterialInventoryLabelFormProps> = ({ id, isEditingChange }) => {
	const navigate = useNavigate();
	const positionOptions: string[] = [];
	for (let position = 1; position <= 30; position++) {
		positionOptions.push(`${position}A`);
		positionOptions.push(`${position}B`);
	}

	const [loadAssignmentFormFields, setLoadAssignmentFormFields] = useState<IMaterialInventoryAssignment>({
		materialInventoryId: +id,
		loadId: -1,
		position: positionOptions[0],
	});

	const [editFormFields, setEditFormFields] = useState<IMaterialInventory | undefined>(undefined);

	const listContext = useListContext();
	const userContext = useUserContext();

	// View mode state
	const [materialInventory, setMaterialInventory] = useState<IMaterialInventory>(null);
	const [selectedLoadId, setSelectedLoadId] = useState<number>(-1);
	const [assignedLoad, setAssignedLoad] = useState<IMaterialInventoryLoad>(null);
	const [isAddingToLoad, setIsAddingToLoad] = useState(false);
	const [isRemovingFromInventory, setIsRemovingFromInventory] = useState(false);
	const [isRemovingFromLoad, setIsRemovingFromLoad] = useState(false);
	const [isLoadingInventory, setIsLoadingInventory] = useState(true);
	const [isLoadingCustomerLoads, setIsLoadingCustomerLoads] = useState(true);
	const [isLoadingAssignedLoad, setIsLoadingAssignedLoad] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [customerLoads, setCustomerLoads] = useState<IMaterialInventoryLoad[]>([]);
	const [isEditing, setIsEditing] = useState(false);

	// Edit mode state
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
	const [isLoadingVendors, setIsLoadingVendors] = useState(true);
	const [isLoadingSources, setIsLoadingSources] = useState(true);
	const [isLoadingMaterialPrices, setIsLoadingMaterialPrices] = useState(true);
	const [isAssignedLoadDeleted, setIsAssignedLoadDeleted] = useState(false);
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [materialOptions, setMaterialOptions] = useState<IMaterialOption[]>([]);
	const [filteredMaterialOptions, setFilteredMaterialOptions] = useState<IMaterialOption[]>([]);
	const [sourceOptions, setSourceOptions] = useState<ISelectOption[]>([]);
	const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(0);
	const [previouslySelectedMaterialIndex, setPreviouslySelectedMaterialIndex] = useState(-1);
	const allowedTilsCustomerIds = userContext.tilsAccess.map(c => c.customerId);
	const [isSaving, setIsSaving] = useState(false);
	const [showCustomerWarning, setShowCustomerWarning] = useState(false);
	const [showVendorWarning, setShowVendorWarning] = useState(false);
	const [showSaveSuccess, setShowSaveSuccess] = useState(false);

	// Formatters for numbers with max allowed and rounded (for money) of precision, rounding half away from zero
	const precFull = AppConfig.precisionDecimalsMaximum;
	const localeFull = new Intl.NumberFormat('en-US', { maximumFractionDigits: precFull, roundingMode: 'halfExpand' });

	let ref = useRef(null);

	useEffect(() => {
		getMaterialInventory();
	}, []);

	useEffect(() => {
		if (materialInventory) {
			getCustomerLoadOptions();
			getCustomerOptions();

			if (materialInventory.remoteShipmentId) {
				getAssignedLoad();
			}
		}
	}, [materialInventory])

	useEffect(() => {
		if (customerLoads.length > 0) {
			if (loadAssignmentFormFields.loadId != selectedLoadId) {
				setSelectedLoadId(customerLoads[0].loadId);
			}
		}
	}, [customerLoads]);

	useEffect(() => {
		setLoadAssignmentFormFields({ ...loadAssignmentFormFields, loadId: selectedLoadId });
	}, [selectedLoadId]);

	useEffect(() => {
		isEditingChange?.(isEditing);
		if (isEditing) {
			setShowCustomerWarning(false);
			setShowVendorWarning(false);
		};
	}, [isEditing])

	useEffect(() => {
		setIsAssignedLoadDeleted(assignedLoad?.status === 'DELETED');
	}, [assignedLoad])

	//#region View mode methods
	const getMaterialInventory = () => {
		setIsLoadingInventory(true);
		LoadService.getMaterialInventory(parseInt(id))
			.then((response) => {
				console.log('LoadService.getMaterialInventory: SUCCESS', response);
				setMaterialInventory(LoadService.mapMaterialInventory(response.data));
			})
			.catch((error) => {
				console.log('LoadService.getMaterialInventory: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingInventory(false);
			});
	}

	const getAssignedLoad = () => {
		setIsLoadingAssignedLoad(true);
		LoadService.getAssignedMaterialInventoryLoad(materialInventory.id)
			.then((response) => {
				console.log('LoadService.getAssignedMaterialInventoryLoad: SUCCESS', response);
				setAssignedLoad(LoadService.mapMaterialInventoryLoad(response.data));
			})
			.catch((error) => {
				console.log('LoadService.getAssignedMaterialInventoryLoad: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingAssignedLoad(false);
			});
	}

	const getCustomerLoadOptions = () => {
		setIsLoadingCustomerLoads(true);
		LoadService.getAssignableLoadsForMaterialInventoryByCustomerId(materialInventory.customerId)
			.then((response) => {
				console.log('LoadService.getAssignableLoadsForMaterialInventoryByCustomerId: SUCCESS', response);
				const loads = LoadService.mapMaterialInventoryLoads(response.data);
				setCustomerLoads(loads);

				if (loads.length > 0) {
					setLoadAssignmentFormFields({ ...loadAssignmentFormFields, ["loadId"]: loads[0].loadId ?? -1 });
				}
				else {
					setLoadAssignmentFormFields({ ...loadAssignmentFormFields, ["loadId"]: -1 });
				}
			})
			.catch((error) => {
				console.log('LoadService.getAssignableLoadsForMaterialInventoryByCustomerId: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingCustomerLoads(false);
			});
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage(undefined);

		// Submit either adds or removes the inventory based on whether it already references a load
		if (materialInventory?.remoteShipmentId) {
			handleRemoveFromLoad();
		}
		else {
			handleAddToLoad();
		}
	}

	const handleAddToLoad = async () => {
		setIsAddingToLoad(true);

		const request: IMaterialInventoryAssignment = {
			materialInventoryId: materialInventory.id,
			loadId: loadAssignmentFormFields.loadId,
			position: loadAssignmentFormFields.position
		};

		LoadService.addMaterialInventoryToLoad(request)
			.then((response) => {
				console.log('LoadService.addMaterialInventoryToLoad: SUCCESS', response);
				setMaterialInventory(LoadService.mapMaterialInventory(response.data));
			})
			.catch((error) => {
				console.log('LoadService.addMaterialInventoryToLoad: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsAddingToLoad(false);
			});
	}

	const handleRemoveFromLoad = async () => {
		setIsRemovingFromLoad(true);

		const request: IMaterialInventoryAssignment = {
			materialInventoryId: materialInventory.id,
			loadId: materialInventory.remoteShipmentId
		};

		LoadService.removeMaterialInventoryFromLoad(request)
			.then((response) => {
				console.log('LoadService.removeMaterialInventoryFromLoad: SUCCESS', response);
				setMaterialInventory(LoadService.mapMaterialInventory(response.data));
			})
			.catch((error) => {
				console.log('LoadService.removeMaterialInventoryFromLoad: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsRemovingFromLoad(false);
			});
	}

	const handleRemoveFromInventory = async () => {
		setErrorMessage(undefined);
		setIsRemovingFromInventory(true);

		LoadService.deleteMaterialInventory(materialInventory.id)
			.then((response) => {
				console.log('LoadService.deleteMaterialInventory: SUCCESS', response);
				navigate('/materialinventory');
			})
			.catch((error) => {
				console.log('LoadService.deleteMaterialInventory: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
				setIsRemovingFromInventory(false);
			});
	}

	const getDocumentTitle = () => {
		if (!materialInventory || !customerLoads) {
			return '';
		}

		const selectedLoad = customerLoads.find(load => load.loadId == selectedLoadId);
		let documentTitle = `Label-MaterialInventory-#${materialInventory?.id}`;
		if (selectedLoad) {
			documentTitle += `-${selectedLoad.trailerNumber}`;
		}
		documentTitle += `-${materialInventory.customerName}`;
		return documentTitle.replaceAll(/[^a-zA-Z0-9#\-]/g, '');
	}

	const print = useReactToPrint({
		contentRef: ref,
		documentTitle: getDocumentTitle(),
		preserveAfterPrint: true,
	});

	const handlePrintLabel = () => {
		print();
	}
	//#endregion View mode methods

	//#region Edit mode methods
	useEffect(() => {
		if (vendorOptions?.length > 0 && !editFormFields?.vendorId) {
			var supportingVendorId = findVendorIdThatSupportsOriginalMaterial();
			if (supportingVendorId) {
				setEditFormFields(prev => ({ ...prev, vendorId: supportingVendorId }));
			}
		}
	}, [vendorOptions])

	useEffect(() => {
		if (editFormFields && editFormFields?.customerId != 0) {
			getVendorOptions(editFormFields.customerId);
			getMaterialOptions();
			getSourceOptions();
		}
	}, [editFormFields?.customerId]);

	useEffect(() => {
		if (!isLoadingVendors && !isLoadingMaterialPrices && editFormFields) {
			var filteredOptions = structuredClone(materialOptions.filter(item => +item.vendorId == editFormFields.vendorId));
			annotateMaterialOptions(filteredOptions);
			setFilteredMaterialOptions(filteredOptions);
		}
	}, [isLoadingVendors, isLoadingMaterialPrices, editFormFields?.vendorId]);

	useEffect(() => {
		if (filteredMaterialOptions.length > 0) {
			var originalMaterialIndex = filteredMaterialOptions.findIndex(m =>
				m.tilsCode == materialInventory.materialId?.toString() && // Match the material ID
				m.uomId == materialInventory.uomId && // Match the UOM				
				m.priceByContainer == !!materialInventory.quantity && // Match the pricing method (only materials priced by containers have quantities)
				(!m.priceByContainer || m.containerId == materialInventory.containerId) // Match the container ID if the material is priced by container

			);
			setSelectedMaterialIndex(originalMaterialIndex > -1 ? originalMaterialIndex : 0);
		}
	}, [filteredMaterialOptions])

	useEffect(() => {
		var option = getSelectedMaterialOption();
		var previousMaterial = previouslySelectedMaterialIndex > -1 ? materialOptions[previouslySelectedMaterialIndex] : undefined;
		if (option) {
			setEditFormFields(prev => ({
				...
				prev,
				["materialId"]: parseInt(filteredMaterialOptions[selectedMaterialIndex].tilsCode),
				["containerId"]: option.priceByContainer == true ? option.containerId : editFormFields?.containerId,
				["uomId"]: option.uomId,
				["quantity"]: option.priceByContainer ? editFormFields?.quantity ?? materialInventory.quantity ?? 1 : undefined
			}));
		}
		setPreviouslySelectedMaterialIndex(selectedMaterialIndex);
	}, [selectedMaterialIndex, filteredMaterialOptions])

	const getCustomerOptions = () => {
		setIsLoadingCustomers(true);
		CustomerService.getCustomers()
			.then((response) => {
				console.log('CustomerService.getCustomers: SUCCESS', response);
				const options: ISelectOption[] = [];
				const activeCustomers = CustomerService
					.mapCustomerLightList(response.data)
					.filter((item) => item.status == 'Active')
					.filter((item) => allowedTilsCustomerIds.includes(+item.id));

				activeCustomers.map((item: ICustomerLight) => {
					if (item.name) {
						options.push({
							"name": item.name,
							"value": item.id
						})
					}
				});
				setCustomerOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('CustomerService.getCustomers: ERROR', error);
			})
			.finally(() => {
				setIsLoadingCustomers(false);
			});
	}

	const getVendorOptions = (customerId: number) => {
		if (!editFormFields?.customerId) return;
		setIsLoadingVendors(true);

		VendorService.getVendorsByCustomerId(customerId)
			.then((response) => {
				console.log('VendorService.getVendorsForCustomer: SUCCESS', response);
				let options: ISelectOption[] = VendorService.mapVendorLightList(response.data).map((item) => {
					return {
						"name": item.name,
						"value": item.id
					}
				});
				setVendorOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					setEditFormFields(prev => ({ ...prev, ["vendorId"]: materialInventory?.vendorId ?? parseInt(options[0].value) }));
				}
				else {
					setEditFormFields(prev => ({ ...prev, ["vendorId"]: -1 }));
				}
			})
			.catch((error) => {
				console.log('VendorService.getVendorsForCustomer: ERROR', error);
			})
			.finally(() => {
				setIsLoadingVendors(false);
			});
	}

	const getMaterialOptions = () => {
		setIsLoadingMaterialPrices(true);
		setEditFormFields(prev => ({ ...prev, ["materialId"]: 0 }));

		MaterialPriceService.getMaterialPricesForCustomer(editFormFields.customerId)
			.then((response) => {
				console.log('MaterialService.getMaterialsForCustomer: SUCCESS', response);
				const options = MaterialPriceService.mapMaterialPricesToMaterialOptions(MaterialPriceService.mapMaterialPrices(response.data));
				setMaterialOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('MaterialService.getMaterialsForCustomer: ERROR', error);
			})
			.finally(() => {
				setIsLoadingMaterialPrices(false);
			});
	}

	/**
	 *  Add annotations to material names if they are priced by container or if there are multiple prices for the same material
	 * @param options A list of IMaterialOptions to annotate
	 */
	const annotateMaterialOptions = (options: IMaterialOption[]) => {
		// Add the container name to each material that is priced by container
		for (let material of options.filter(o => o.priceByContainer == true)) {
			material.name = material.name + ` (by ${getContainerById(material.containerId)?.name ?? "container"})`;
		}

		// Differetiate between units of measure if there are material prices with the same material name priced by weight
		for (let materialId of Array.from(new Set(options.map(m => m.tilsCode)))) {
			const optionsForMaterial = options.filter(o => o.tilsCode == materialId.toString() && o.priceByContainer != true);
			if (optionsForMaterial.length > 1) {
				for (let material of optionsForMaterial) {
					material.name = material.name + ` (by ${material.uom})`;
				}
			}
		}
	}

	const getSourceOptions = () => {
		setIsLoadingSources(true);
		CustomerService.getActiveCustomerLocationsByCustomerId(editFormFields?.customerId)
			.then((response) => {
				console.log('CustomerService.getActiveCustomerLocationsByCustomerId: SUCCESS', response);
				const locations = CustomerService.mapCustomerLocationOptions(response.data).sort((a, b) => UtilityService.compareStrings(a.name, b.name));
				setSourceOptions(locations);
			})
			.catch((error) => {
				console.log('CustomerService.getActiveCustomerLocationsByCustomerId: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsLoadingSources(false);
			});
	};

	const getContainerById = (id: number) => {
		return listContext.containers.find(c => c.id == id);
	}

	const getUomIdFromName = (name: string) => {
		return +listContext.unitsOfMeasurement.find(u => u.name == name)?.value || undefined;
	}

	const getSelectedMaterialOption = (): IMaterialOption | undefined => {
		return filteredMaterialOptions[selectedMaterialIndex];
	}

	const handleUomChange = (event: SelectChangeEvent<number>) => {
		const selectedUomId = Number.parseInt(event.target.value.toString());
		setEditFormFields(prev => ({ ...prev, ["uomId"]: selectedUomId }));
	}

	const handleEditInventory = () => {
		const editInventory = JSON.parse(JSON.stringify(materialInventory));
		setEditFormFields(editInventory);
		setIsEditing(true);
	}

	const handleEditCancel = () => {
		setIsEditing(false);
		setEditFormFields(undefined);
	}

	const handleEditSave = () => {
		setIsSaving(true);
		setErrorMessage(undefined);
		setShowSaveSuccess(false);

		LoadService.updateMaterialInventory(editFormFields)
			.then((response) => {
				console.log('LoadService.updateMaterialInventory: SUCCESS', response);
				setMaterialInventory(LoadService.mapMaterialInventory(response.data));
				setIsEditing(false);
				setEditFormFields(undefined);
				setShowSaveSuccess(true);
			})
			.catch((error) => {
				console.log('LoadService.updateMaterialInventory: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo({ top: 0, behavior: 'smooth' });
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	const doesCustomerSupportOriginalMaterial = () => {
		if (!editFormFields?.customerId || !materialInventory.materialId) return false;
		return materialOptions.some(m => m.tilsCode == materialInventory.materialId.toString());
	}

	const findVendorIdThatSupportsOriginalMaterial = (): number | undefined => {
		for (let vendor of vendorOptions) {
			if (doesVendorSupportOriginalMaterial(+vendor.value)) {
				return +vendor.value;
			}
		}
		return undefined;
	}

	const isVendorDisabled = () => {
		return isLoadingVendors || (!!materialInventory?.remoteShipmentId && (doesVendorSupportOriginalMaterial(editFormFields?.vendorId)))
	}

	const doesVendorSupportOriginalMaterial = (useVendorId: number = null) => {
		if (!(useVendorId ?? editFormFields?.vendorId) || !materialInventory.materialId) return false;
		let materialOptionsForVendor = filteredMaterialOptions;
		if (useVendorId) {
			materialOptionsForVendor = materialOptions.filter(m => +m.vendorId == useVendorId &&
				(!m.priceByContainer || m.containerId == materialInventory.containerId) &&
				(m.priceByContainer || m.uomId == materialInventory.uomId));
		}
		return materialOptionsForVendor.some(m => m.tilsCode == materialInventory.materialId.toString());
	}
	//#endregion Edit mode methods

	return (
		<section className="hwi-material-inventory-label-form">
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			{isAssignedLoadDeleted &&
				<Alert className="alert-warning"
					severity="warning">
					This inventory cannot be modified because the load it was assigned to has been deleted.
				</Alert>
			}
			{showSaveSuccess &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setShowSaveSuccess(false); }} size='small'>X</IconButton>}>
					Your changes have been saved. You may need to refresh the load screen to see them.
				</Alert>
			}
			{materialInventory?.modifiedDate &&
				<Alert className="alert-warning"
					severity="warning">
					This inventory was last modified on <strong>{dayjs(materialInventory.modifiedDate).utc(true).tz(dayjs.tz.guess()).format('M/D/YYYY h:mm A')}</strong>. Any labels created before this date should be replaced.
				</Alert>
			}
			{userContext.permissions.canEditMaterialInventory && assignedLoad && assignedLoad?.status !== "Loading" && assignedLoad?.status !== "DELETED" &&
				<Alert className="alert-warning"
					severity="warning">
					BOL# {assignedLoad?.hwiNumber} is in "{assignedLoad?.status}" status. Inventories assigned to loads can only be edited if the load is in "Loading" status.
				</Alert>
			}
			<Spinner dependency={isLoadingInventory || isLoadingCustomerLoads || isLoadingAssignedLoad} text="Loading Inventory...">
				{!isEditing &&
					<Form onSubmit={(event) => handleSubmit(event)}>
						<div className="row">
							<div className="col-load">
								<InputLabel>Load</InputLabel>
								{materialInventory?.remoteShipmentId ? (
									<Select
										name="load"
										className="load-field"
										value={assignedLoad?.loadId ?? ""}
										required
										fullWidth
										disabled={true}
										onChange={(event) => { setSelectedLoadId(parseInt(event.target.value.toString())) }}
									>
										<MenuItem value={assignedLoad?.loadId}>Trailer {assignedLoad?.trailerNumber} to {assignedLoad?.customerName}</MenuItem>
									</Select>
								) : (
									<Select
										name="load"
										className="load-field"
										value={selectedLoadId == -1 ? "" : selectedLoadId}
										required
										fullWidth
										disabled={isAddingToLoad || customerLoads.length == 0}
										onChange={(event) => { setSelectedLoadId(parseInt(event.target.value.toString())) }}
									>
										{customerLoads.map((item, index) => (
											<MenuItem value={item.loadId} key={index}>Trailer {item.trailerNumber} to {item.customerName}</MenuItem>
										))}
										{customerLoads.length == 0 &&
											<MenuItem value={-1}>There are no available loads for this customer.</MenuItem>
										}
									</Select>
								)}
							</div>
							<div className="col-position">
								<InputLabel>Position</InputLabel>
								{materialInventory?.remoteShipmentId ? (
									<Select
										className="container-field"
										value={materialInventory?.remoteShipmentPosition}
										required
										fullWidth
										disabled={true}
									>
										<MenuItem value={materialInventory?.remoteShipmentPosition}>{materialInventory?.remoteShipmentPosition}</MenuItem>
									</Select>
								) : (
									<Select
										className="container-field"
										value={positionOptions.length > 0 ? loadAssignmentFormFields.position : "N/A"}
										required
										fullWidth
										disabled={isAddingToLoad || positionOptions.length == 0}
										onChange={(event) => { setLoadAssignmentFormFields({ ...loadAssignmentFormFields, position: event.target.value }); }}
									>
										{positionOptions.map((item, index) => (
											<MenuItem value={item} key={index}>{item}</MenuItem>
										))}
										{positionOptions.length == 0 &&
											<MenuItem value={"N/A"}>N/A</MenuItem>
										}
									</Select>
								)}
							</div>

							<div className="col-add">
								{!materialInventory?.remoteShipmentId &&
									<Button variant="contained" fullWidth type="submit" color="primary" disabled={isAddingToLoad || isRemovingFromLoad || isRemovingFromInventory || selectedLoadId < 1 || loadAssignmentFormFields.position === ''}>{isAddingToLoad ? "Adding..." : "Add to Load"}</Button>
								}
								{materialInventory?.remoteShipmentId &&
									<Button variant="contained" fullWidth type="submit" color="error" disabled={isRemovingFromLoad || isAssignedLoadDeleted || materialInventory.remoteShipmentStatus !== 'Loading'}>{isRemovingFromLoad ? "Unloading..." : "Unload"}</Button>
								}
							</div>
						</div>
					</Form>
				}
				{!isEditing || !editFormFields ? (
					<div className="label-container">
						<div className="fields">
							<div className="row">
								<InputLabel>Customer</InputLabel>
								<TextField className="customer-field"
									value={materialInventory?.customerName ?? 'N/A'}
									type="text"
									disabled={true}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Material (Waste Category - Name)</InputLabel>
								<TextField className="material-field"
									value={materialInventory?.materialName ?? 'N/A'}
									type="text"
									disabled={true}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Container</InputLabel>
								<TextField className="container-field"
									value={`${materialInventory?.containerName ?? 'N/A'}${(materialInventory?.quantity > 1) ? ` x ${materialInventory.quantity}` : ''}`}
									type="text"
									disabled={true}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Source (Location)</InputLabel>
								<TextField className="source-field"
									value={materialInventory?.customerLocationName ?? 'N/A'}
									type="text"
									disabled={true}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>Weight</InputLabel>
								<TextField className="weight-field"
									value={`${materialInventory?.weight == undefined ? "N/A" : localeFull.format(materialInventory?.weight)} ${materialInventory?.weight ? (materialInventory?.uomName ?? '(UNKNOWN UNIT)') : ''}`}
									type="text"
									disabled={true}
								></TextField>
							</div>
							<div className="row">
								<InputLabel>TILS Load</InputLabel>
								<p className="load-id-field">
									{materialInventory?.remoteShipmentId ? (
										assignedLoad?.status != "DELETED" ?
											(
												<NavLink to={"/tils/" + materialInventory.remoteShipmentId} className="tils-link">
													<Button>
														<Icons.ViewItem fill="#434D76" height="16px"></Icons.ViewItem>
														<span>View Load</span>
													</Button>
												</NavLink>
											) : (
												<span className='deleted'>Deleted load {assignedLoad?.hwiNumber} cannot be viewed.</span>
											)
									) : (
										<span className='unassigned'>Unassigned</span>
									)}
								</p>
							</div>
							{isEditing ? (
								<Button variant="contained" fullWidth type="button" color="error" disabled={false} onClick={handleEditCancel}>Cancel</Button>
							) : (
								<div className="row controls">
									<Button variant="contained" fullWidth type="button" color="primary" className='print' disabled={isAddingToLoad || isRemovingFromLoad || isRemovingFromInventory} onClick={() => handlePrintLabel()}>Print Label</Button>
									{userContext.permissions.canEditMaterialInventory && (!assignedLoad || assignedLoad?.status === "Loading") &&
										<Button variant="contained" fullWidth type="button" color="primary" className='print' disabled={isAddingToLoad || isRemovingFromLoad || isRemovingFromInventory || isAssignedLoadDeleted} onClick={() => handleEditInventory()}>Edit Inventory</Button>
									}
									<Button variant="contained" fullWidth type="button" color="error" disabled={!!materialInventory?.remoteShipmentId || isAddingToLoad || isRemovingFromInventory || isAssignedLoadDeleted} onClick={() => handleRemoveFromInventory()}>{isRemovingFromInventory ? "Deleting..." : "Delete Item"}</Button>
								</div>
							)}
						</div>

						<div className="qr" ref={ref}>
							{!isEditing &&
								<>
									<div className="id-number">
										<div>
											ID:<span className='number'>{id.toString().padStart(6, '0')}</span>
										</div>
									</div>
									{materialInventory?.modifiedDate &&
										<div className="modified-date">
											REVISED ON: {dayjs(materialInventory.modifiedDate).utc(true).tz(dayjs.tz.guess()).format("MM/DD/YYYY HH:mm:ss")}
										</div>
									}
									<div className="graphic">
										<QRCode value={JSON.stringify(materialInventory)} size={300}></QRCode>
									</div>
									<div className="material-name">
										<p>
											{materialInventory?.materialName?.toUpperCase() ?? 'UNKNOWN MATERIAL'}
										</p>
									</div>
									<div className="weight">
										<p>
											{materialInventory?.weight?.toLocaleString() ?? 'UNKNOWN WEIGHT'}
											{materialInventory?.weight && <> {materialInventory?.uomName?.toUpperCase() ?? '(UNKNOWN UNIT)'}</>}
											{materialInventory?.weight && materialInventory?.uomName != "pounds" && (materialInventory?.uomLbsConversion ?? 1) != 1 &&
												<span>&nbsp;({(materialInventory?.weight * materialInventory?.uomLbsConversion)?.toLocaleString()} POUNDS)</span>
											}
										</p>
									</div>
									<div className="weight">
										<p>{materialInventory?.creationDate ? moment(materialInventory?.creationDate).format("MM/DD/yyyy") : ""}</p>
									</div>
								</>
							}
						</div>
					</div>
				) : (
					<div className="edit-material-form">
						<div className="row">
							<InputLabel>{(isLoadingCustomers) ? "Customer * (Loading...)" : "Customer *"}</InputLabel>
							<Select
								value={editFormFields?.customerId < 1 ? '' : editFormFields.customerId}
								className="customer-field"
								required
								onChange={(event) => { setEditFormFields(prev => ({ ...prev, ["customerId"]: Number.parseInt(event.target.value.toString()) })) }}
								disabled={isLoadingCustomers || !!materialInventory?.remoteShipmentId}
								onMouseDown={() => setShowCustomerWarning(true)}
							>
								{customerOptions.map((item, index) => (
									<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
								))}
							</Select>
							{!!materialInventory?.remoteShipmentId && showCustomerWarning && <div className="comment">Customer cannot be modified after an inventory has been added to a load. You must unload this inventory to change its customer.</div>}
						</div>
						<div className="row">
							<InputLabel>{(isLoadingVendors) ? "Vendor * (Loading...)" : "Vendor *"}</InputLabel>
							<Select
								value={editFormFields?.vendorId ?? ''}
								className="vendor-field"
								required
								onChange={(event) => { setEditFormFields(prev => ({ ...prev, ["vendorId"]: Number.parseInt(event.target.value.toString()) })) }}
								disabled={isVendorDisabled()}
								onMouseDown={() => { if (isVendorDisabled()) setShowVendorWarning(true) }}
							>
								{vendorOptions.map((item, index) => (
									<MenuItem value={item.value} key={index}>{item.name}{(!doesVendorSupportOriginalMaterial(null) && doesVendorSupportOriginalMaterial(+item.value)) ? <span className='supports-original-material'>&nbsp;&nbsp;(Supports original material)</span> : ''}</MenuItem>
								))}
							</Select>
							{!editFormFields?.vendorId && !isLoadingVendors && <div className="comment-error">Vendor is missing from this inventory. Please select one to view supported materials.</div>}
							{!!materialInventory?.remoteShipmentId && showVendorWarning && <div className="comment">Vendor cannot be modified after an inventory has been added to a load. You must unload this inventory to change its vendor.</div>}
						</div>
						<div className="row">
							<InputLabel>{(isLoadingMaterialPrices) ? "Material (Waste Category - Name) * (Loading...)" : "Material (Waste Category - Name) *"}</InputLabel>
							<Select
								value={selectedMaterialIndex}
								className="material-field"
								required
								onChange={(event) => { setSelectedMaterialIndex(parseInt(event.target.value.toString())) }}
								disabled={isLoadingMaterialPrices || !editFormFields?.vendorId}
							>
								{filteredMaterialOptions.map((item, index) => (
									<MenuItem value={index} key={index}>{item.name}</MenuItem>
								))}
							</Select>
							{!isLoadingVendors && !isLoadingMaterialPrices && editFormFields?.vendorId > 0 && !doesVendorSupportOriginalMaterial(editFormFields?.vendorId) &&
								<div className="comment">Vendor does not support originally selected material <strong>{materialInventory.materialName}</strong>. To continue using the original material, select a vendor that supports it.</div>
							}
						</div>
						<div className="row">
							<InputLabel>Container *</InputLabel>
							<Select
								value={editFormFields?.containerId < 1 ? '' : editFormFields.containerId}
								className="container-field"
								required
								disabled={selectedMaterialIndex > -1 && getSelectedMaterialOption()?.priceByContainer == true}
								onChange={(event) => { setEditFormFields(prev => ({ ...prev, ["containerId"]: Number.parseInt(event.target.value.toString()) })) }}
							>
								{listContext.containers.map((item, index) => (
									<MenuItem value={item.id} key={index}>{item.name}</MenuItem>
								))}
							</Select>
						</div>
						{getSelectedMaterialOption()?.priceByContainer &&
							<div className="row">
								<InputLabel>Quantity *</InputLabel>
								<NumberField className="quantity-field"
									value={editFormFields?.quantity ?? 1}
									name="quantity"
									type="number"
									required
									min={1}
									step={1}
									onBlur={(event) => { setEditFormFields(prev => ({ ...prev, ["quantity"]: parseInt(event.target.value) })) }}
									onFocus={(event) => event.target.select()}
								></NumberField>
							</div>
						}
						<div className="row">
							<InputLabel>{(isLoadingSources) ? "Source (Location) (Loading...)" : "Source (Location)"}</InputLabel>
							<Select
								value={editFormFields?.customerLocationId ?? 0}
								className="source-field"
								onChange={(event) => { setEditFormFields(prev => ({ ...prev, ["customerLocationId"]: Number.parseInt(event.target.value.toString()) })) }}
								disabled={isLoadingSources}
							>
								<MenuItem value={0} key={0}>None</MenuItem>
								{sourceOptions.map((item, index) => (
									<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
								))}
							</Select>
							{(editFormFields?.customerLocationId == -1) &&
								<p className="help-text">The selected customer does not have any associated inventory locations.</p>
							}
						</div>
						<div className="row">
							<InputLabel>Gross Weight *</InputLabel>
							<NumberField className="weight-field"
								value={editFormFields?.weight ?? ''}
								name="weight"
								type="number"
								required
								min={0}
								step={1}
								precision={precFull}
								onBlur={(event) => { setEditFormFields(prev => ({ ...prev, ["weight"]: parseFloat(event.target.value) })) }}
								onFocus={(event) => event.target.select()}
							></NumberField>
						</div>
						<div className="row">
							<InputLabel>Unit of Measure *</InputLabel>
							<Select
								value={editFormFields?.uomId}
								className="unit-field"
								required
								disabled={selectedMaterialIndex > -1 && getSelectedMaterialOption()?.priceByContainer != true}
								onChange={handleUomChange}
							>
								{listContext.unitsOfMeasurement.map((item, index) => (
									<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
								))}
							</Select>
						</div>
						<div className="row controls">
							<Button type="button" color="primary" className="save-button" variant="contained" disabled={isSaving} onClick={handleEditSave}>{isSaving ? "Saving..." : "Save"}</Button>
							<Button type="button" color="primary" className="cancel-button" variant="outlined" disabled={isSaving} onClick={handleEditCancel}>Cancel</Button>
						</div>
					</div>
				)}
				{!isEditing &&
					<div className="load-position-map">
						<img src={loadPositionMap} alt="Image" />
					</div>
				}
			</Spinner>
			{/* <Json data={materialInventory}></Json>
			<Json data={selectedMaterialIndex}></Json>
			<Json data={filteredMaterialOptions}></Json> */}
		</section>
	)
}

export default MaterialInventoryLabelForm;